/**
 * PasswordSetupPage - SPEC §5 customer first-login flow.
 *
 * Reads `?token=...` from the URL (24-hour single-use), validates the
 * password client-side against the same policy the backend enforces
 * (`backend/app/services/password_service.py:validate_password_strength`),
 * POSTs to `/auth/password-setup`, and on success the backend returns
 * tokens + sets the refresh cookie - we drop the user straight onto
 * the home page.
 *
 * This page is NEW for Buyist (Auware does not have it). The visual
 * shape mirrors Auware's ResetPasswordPage so the styling matches.
 */
import { type FormEvent, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import Button from "@/components/ui/Button";
import { useAuthStore } from "@/stores/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import api from "@/lib/api";

interface PasswordCheck {
  label: string;
  met: boolean;
}

function getPasswordChecks(password: string): PasswordCheck[] {
  return [
    { label: "At least 12 characters", met: password.length >= 12 },
    { label: "Has uppercase letter", met: /[A-Z]/.test(password) },
    { label: "Has lowercase letter", met: /[a-z]/.test(password) },
    { label: "Has a digit", met: /\d/.test(password) },
  ];
}

function extractUser(data: Record<string, unknown>) {
  const u = data.user as Record<string, unknown>;
  return {
    id: u.id as string,
    email: u.email as string,
    name: u.full_name as string,
    avatarUrl: u.avatar_url as string | undefined,
    role: u.role as "customer" | "agent" | "system_admin" | undefined,
    isSystemAdmin: u.is_system_admin as boolean | undefined,
    isAgent: u.is_agent as boolean | undefined,
    mfaEnabled: u.mfa_enabled as boolean | undefined,
    hasPasskeys: u.has_passkeys as boolean | undefined,
    isEmailVerified: u.is_email_verified as boolean | undefined,
  };
}

export function PasswordSetupPage(): JSX.Element {
  usePageTitle("Set up your password");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useAuthStore((s) => s.login);
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const passwordChecks = useMemo(() => getPasswordChecks(password), [password]);
  const allPassed = passwordChecks.every((c) => c.met);

  if (!token) {
    return (
      <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4 py-12">
        <div className="w-full max-w-md space-y-8 text-center">
          <img
            src="/buyist-logo.png"
            alt="Buyist Support"
            className="mx-auto h-14 w-auto"
          />
          <div className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-neutral-600">
              Invalid setup link. Please request a new one.
            </p>
            <Link
              to="/forgot-password"
              className="block text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              Request a new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!allPassed) {
      setError("Password does not meet the requirements.");
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post("/auth/password-setup", {
        token,
        new_password: password,
      });

      const user = extractUser(data);
      const tokens = {
        accessToken: data.access_token as string,
        refreshToken: data.refresh_token as string,
      };

      login(user, tokens);
      navigate("/", { replace: true });
    } catch (err: unknown) {
      const detail = (err as {
        response?: { data?: { detail?: string } };
      })?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Setup failed. The link may have expired - please request a new one.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img
            src="/buyist-logo.png"
            alt="Buyist Support"
            className="mx-auto h-14 w-auto"
          />
          <h2 className="mt-2 text-xl font-semibold text-neutral-900">
            Set up your password
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            Welcome to Buyist Support. Choose a password to finish setting up
            your account.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-6 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm"
        >
          {error && (
            <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="new-password"
              className="block text-sm font-medium text-neutral-700"
            >
              New password
            </label>
            <input
              id="new-password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Min. 12 characters"
              autoFocus
            />
            {password.length > 0 && (
              <ul className="mt-2 space-y-1">
                {passwordChecks.map((check) => (
                  <li
                    key={check.label}
                    className={`flex items-center gap-1.5 text-xs ${
                      check.met ? "text-emerald-600" : "text-neutral-400"
                    }`}
                  >
                    <span
                      className="inline-block h-1 w-4 rounded-full"
                      style={{
                        backgroundColor: check.met ? "#16a34a" : "#d4d4d4",
                      }}
                    />
                    {check.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label
              htmlFor="confirm-password"
              className="block text-sm font-medium text-neutral-700"
            >
              Confirm password
            </label>
            <input
              id="confirm-password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Re-enter your password"
            />
          </div>

          <Button type="submit" className="w-full" isLoading={loading}>
            Set password and sign in
          </Button>
        </form>
      </div>
    </div>
  );
}

export default PasswordSetupPage;
